import React, { useState, useMemo } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, ZAxis, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell, Legend, CartesianGrid,
} from 'recharts';
import {
  CheckSquare, Square, TrendUp, Leaf, CurrencyDollar,
  Factory, ChartLineUp, Target
} from '@phosphor-icons/react';

const CATEGORY_COLORS = {
  'Tariff Optimization': '#4ade80',
  'Utility Efficiency': '#60a5fa',
  'Energy Storage': '#facc15',
  Maintenance: '#94a3b8',
  Quality: '#f97316',
  Combined: '#c084fc',
  Custom: '#e879f9',
};

function fmtVnd(n) {
  if (n >= 1_000_000_000) return `${(n / 1_000_000_000).toFixed(1)}B`;
  if (n >= 1_000_000) return `${(n / 1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `${(n / 1_000).toFixed(0)}K`;
  return n?.toLocaleString?.() ?? n;
}

function MatrixTooltip({ active, payload }) {
  if (!active || !payload?.length) return null;
  const d = payload[0]?.payload;
  if (!d) return null;
  return (
    <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body max-w-xs">
      <p className="font-semibold text-text-primary mb-1.5">{d.title}</p>
      <p className="text-text-secondary">{d.category}</p>
      <div className="mt-1.5 space-y-0.5">
        <p className="text-emerald-400">OEE: +{d.oee} pp</p>
        <p className="text-amber-400">Cost: {fmtVnd(d.vnd)} VND/day</p>
        <p className="text-teal-400">CO2e: -{d.co2}kg/day</p>
        <p className="text-text-secondary">Efficiency: {d.efficiency?.toFixed(0)} VND per OEE pp</p>
      </div>
    </div>
  );
}

export default function OeePortfolio({ data }) {
  const prebuilt = data?.scenarios || [];
  const custom = data?.custom_scenarios || [];
  const kpis = data?.kpis || {};

  // Normalize custom scenarios to same shape as prebuilt
  const allScenarios = useMemo(() => {
    const normalized = prebuilt.map(s => ({
      id: s.scenario_id,
      title: s.title,
      category: s.category,
      oee: s.delta.oee_points,
      vnd: s.delta.vnd_saved,
      co2: s.delta.co2e_reduced_kg,
      source: 'preset',
    }));
    custom.forEach(s => {
      const d = s.projections?.delta;
      if (d) {
        normalized.push({
          id: s.scenario_id,
          title: s.title,
          category: 'Custom',
          oee: d.oee_points || 0,
          vnd: d.vnd_saved || 0,
          co2: d.co2e_reduced_kg || 0,
          source: 'custom',
        });
      }
    });
    return normalized;
  }, [prebuilt, custom]);

  const [selected, setSelected] = useState(() => new Set(allScenarios.map(s => s.id)));

  const toggle = (id) => {
    setSelected(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const selectAll = () => setSelected(new Set(allScenarios.map(s => s.id)));
  const selectNone = () => setSelected(new Set());

  const selectedScenarios = useMemo(
    () => allScenarios.filter(s => selected.has(s.id)),
    [allScenarios, selected]
  );

  // Cumulative totals
  const cumOee = selectedScenarios.reduce((a, s) => a + s.oee, 0);
  const cumVnd = selectedScenarios.reduce((a, s) => a + s.vnd, 0);
  const cumCo2 = selectedScenarios.reduce((a, s) => a + s.co2, 0);
  const projectedOee = Math.min(kpis.plant_oee_pct + cumOee, 100);
  const cumVndMonth = cumVnd * 30;

  // Scatter data: X = VND saved, Y = OEE gain, Z (size) = CO2 reduced
  const scatterData = allScenarios.map(s => ({
    ...s,
    efficiency: s.oee > 0 ? s.vnd / s.oee : 0,
    isSelected: selected.has(s.id),
  }));

  // Stacked bar data by category
  const catMap = {};
  selectedScenarios.forEach(s => {
    if (!catMap[s.category]) catMap[s.category] = { category: s.category, oee: 0, vnd: 0, co2: 0, count: 0 };
    catMap[s.category].oee += s.oee;
    catMap[s.category].vnd += s.vnd;
    catMap[s.category].co2 += s.co2;
    catMap[s.category].count += 1;
  });
  const categoryData = Object.values(catMap).sort((a, b) => b.vnd - a.vnd);

  // Ranking: ROI efficiency = VND saved per OEE point
  const ranked = [...allScenarios]
    .map(s => ({ ...s, efficiency: s.oee > 0 ? s.vnd / s.oee : 0 }))
    .sort((a, b) => b.efficiency - a.efficiency);

  return (
    <div className="space-y-5 animate-fade-in" data-testid="oee-portfolio">
      {/* Cumulative Impact Strip */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
          <div>
            <h2 className="font-heading text-base font-semibold text-text-primary">Cumulative Portfolio Impact</h2>
            <p className="text-xs font-body text-text-secondary mt-0.5">{selectedScenarios.length} of {allScenarios.length} scenarios selected</p>
          </div>
          <div className="flex gap-2">
            <button onClick={selectAll} data-testid="oee-portfolio-select-all"
              className="px-3 py-1.5 rounded-md text-xs font-body font-medium border border-border text-text-secondary hover:border-primary hover:text-primary transition-all">
              Select All
            </button>
            <button onClick={selectNone} data-testid="oee-portfolio-clear"
              className="px-3 py-1.5 rounded-md text-xs font-body font-medium border border-border text-text-secondary hover:border-red-400 hover:text-red-400 transition-all">
              Clear
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
          <div className="bg-background rounded-lg p-3 text-center">
            <Factory size={16} className="text-primary mx-auto mb-1" weight="duotone" />
            <p className="font-heading text-xl font-bold text-primary">{kpis.plant_oee_pct}%</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Current OEE</p>
          </div>
          <div className="bg-background rounded-lg p-3 text-center border border-emerald-500/20">
            <TrendUp size={16} className="text-emerald-400 mx-auto mb-1" weight="duotone" />
            <p className="font-heading text-xl font-bold text-emerald-400">{projectedOee.toFixed(1)}%</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">Projected OEE</p>
          </div>
          <div className="bg-background rounded-lg p-3 text-center">
            <ChartLineUp size={16} className="text-amber-400 mx-auto mb-1" weight="duotone" />
            <p className="font-heading text-xl font-bold text-amber-400">+{cumOee.toFixed(1)} pp</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">OEE Uplift</p>
          </div>
          <div className="bg-background rounded-lg p-3 text-center">
            <CurrencyDollar size={16} className="text-yellow-400 mx-auto mb-1" weight="duotone" />
            <p className="font-heading text-xl font-bold text-yellow-400">{fmtVnd(cumVnd)}</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">VND Saved/Day</p>
          </div>
          <div className="bg-background rounded-lg p-3 text-center">
            <CurrencyDollar size={16} className="text-amber-400 mx-auto mb-1" weight="duotone" />
            <p className="font-heading text-xl font-bold text-amber-400">{fmtVnd(cumVndMonth)}</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">VND Saved/Month</p>
          </div>
          <div className="bg-background rounded-lg p-3 text-center">
            <Leaf size={16} className="text-teal-400 mx-auto mb-1" weight="duotone" />
            <p className="font-heading text-xl font-bold text-teal-400">-{cumCo2}kg</p>
            <p className="text-[10px] font-body text-text-secondary uppercase tracking-wider">CO2e/Day</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Left: ROI Prioritization Matrix (scatter) */}
        <div className="lg:col-span-2 bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-1">ROI Prioritization Matrix</h3>
          <p className="text-xs font-body text-text-secondary mb-4">X: Cost savings/day — Y: OEE improvement — Bubble size: CO2e reduction</p>
          <div className="h-72">
            <ResponsiveContainer width="100%" height="100%">
              <ScatterChart margin={{ top: 10, right: 20, bottom: 10, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="vnd" type="number" name="VND/day" tick={{ fill: '#A3A380', fontSize: 11 }}
                  tickFormatter={v => fmtVnd(v)} label={{ value: 'Cost Savings (VND/day)', position: 'bottom', fill: '#A3A380', fontSize: 10, dy: 5 }} />
                <YAxis dataKey="oee" type="number" name="OEE pp" tick={{ fill: '#A3A380', fontSize: 11 }}
                  label={{ value: 'OEE Gain (pp)', angle: -90, position: 'insideLeft', fill: '#A3A380', fontSize: 10 }} />
                <ZAxis dataKey="co2" range={[80, 600]} name="CO2e" />
                <Tooltip content={<MatrixTooltip />} />
                <Scatter data={scatterData}>
                  {scatterData.map((entry, idx) => (
                    <Cell key={idx}
                      fill={entry.isSelected ? (CATEGORY_COLORS[entry.category] || '#6b7280') : '#333'}
                      stroke={entry.isSelected ? '#fff' : '#555'}
                      strokeWidth={entry.isSelected ? 1.5 : 0.5}
                      opacity={entry.isSelected ? 1 : 0.35}
                    />
                  ))}
                </Scatter>
              </ScatterChart>
            </ResponsiveContainer>
          </div>
          {/* Category Legend */}
          <div className="flex flex-wrap gap-3 mt-3">
            {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
              <span key={cat} className="flex items-center gap-1.5 text-[10px] font-body text-text-secondary">
                <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: color }} /> {cat}
              </span>
            ))}
          </div>
        </div>

        {/* Right: Scenario Selector Checklist */}
        <div className="bg-surface border border-border rounded-lg p-5 overflow-y-auto max-h-[460px]">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-3">Scenario Selection</h3>
          <div className="space-y-1.5">
            {allScenarios.map(s => {
              const isOn = selected.has(s.id);
              const catColor = CATEGORY_COLORS[s.category] || '#6b7280';
              return (
                <button key={s.id} onClick={() => toggle(s.id)}
                  data-testid={`oee-portfolio-toggle-${s.id}`}
                  className={`w-full text-left flex items-start gap-2.5 p-2.5 rounded-lg border transition-all ${
                    isOn ? 'border-primary/40 bg-primary/5' : 'border-border/50 bg-transparent hover:border-border'
                  }`}>
                  {isOn
                    ? <CheckSquare size={16} className="text-primary shrink-0 mt-0.5" weight="fill" />
                    : <Square size={16} className="text-text-secondary shrink-0 mt-0.5" />}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-body font-semibold text-text-primary truncate">{s.title}</span>
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className="text-[9px] px-1.5 py-0.5 rounded-full font-body" style={{ backgroundColor: catColor + '20', color: catColor }}>
                        {s.category}
                      </span>
                      <span className="text-[10px] font-body text-emerald-400">+{s.oee}pp</span>
                      <span className="text-[10px] font-body text-amber-400">{fmtVnd(s.vnd)}</span>
                      <span className="text-[10px] font-body text-teal-400">-{s.co2}kg</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>

      {/* Category Breakdown Bar Chart */}
      {categoryData.length > 0 && (
        <div className="bg-surface border border-border rounded-lg p-5">
          <h3 className="font-heading text-sm font-semibold text-text-primary mb-1">Impact by Category</h3>
          <p className="text-xs font-body text-text-secondary mb-4">Selected scenarios grouped by initiative category</p>
          <div className="h-56">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={categoryData} margin={{ left: 0, right: 20 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#2a2a2a" />
                <XAxis dataKey="category" tick={{ fill: '#A3A380', fontSize: 11 }} />
                <YAxis tick={{ fill: '#A3A380', fontSize: 11 }} />
                <Tooltip content={({ active, payload }) => {
                  if (!active || !payload?.length) return null;
                  const d = payload[0]?.payload;
                  return (
                    <div className="bg-[#1a1a1a] border border-border rounded-lg p-3 shadow-lg text-xs font-body">
                      <p className="font-semibold text-text-primary mb-1">{d?.category} ({d?.count} scenarios)</p>
                      <p className="text-emerald-400">OEE: +{d?.oee?.toFixed(1)} pp</p>
                      <p className="text-amber-400">VND: {fmtVnd(d?.vnd)}/day</p>
                      <p className="text-teal-400">CO2e: -{d?.co2}kg/day</p>
                    </div>
                  );
                }} />
                <Legend />
                <Bar dataKey="oee" name="OEE Gain (pp)" radius={[4, 4, 0, 0]} barSize={24}>
                  {categoryData.map((entry, idx) => (
                    <Cell key={idx} fill={CATEGORY_COLORS[entry.category] || '#6b7280'} />
                  ))}
                </Bar>
                <Bar dataKey="co2" name="CO2e Reduced (kg)" fill="#38bdf8" radius={[4, 4, 0, 0]} barSize={24} opacity={0.7} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      {/* ROI Ranking Table */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-1">ROI Efficiency Ranking</h3>
        <p className="text-xs font-body text-text-secondary mb-4">Scenarios ranked by VND saved per OEE percentage point gained</p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm font-body" data-testid="oee-portfolio-ranking-table">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">#</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Scenario</th>
                <th className="text-left py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Category</th>
                <th className="text-right py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">OEE</th>
                <th className="text-right py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">VND/Day</th>
                <th className="text-right py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">CO2e/Day</th>
                <th className="text-right py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">VND/OEE pp</th>
                <th className="text-center py-2 px-3 text-[10px] font-semibold uppercase tracking-wider text-text-secondary">Active</th>
              </tr>
            </thead>
            <tbody>
              {ranked.map((s, idx) => {
                const isOn = selected.has(s.id);
                const catColor = CATEGORY_COLORS[s.category] || '#6b7280';
                return (
                  <tr key={s.id} className={`border-b border-border/40 transition-colors ${isOn ? 'bg-primary/5' : 'hover:bg-white/[0.02]'}`}>
                    <td className="py-2.5 px-3 font-heading font-bold text-text-secondary">{idx + 1}</td>
                    <td className="py-2.5 px-3">
                      <p className="font-medium text-text-primary text-xs">{s.title}</p>
                    </td>
                    <td className="py-2.5 px-3">
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium" style={{ backgroundColor: catColor + '20', color: catColor }}>
                        {s.category}
                      </span>
                    </td>
                    <td className="py-2.5 px-3 text-right text-xs font-semibold text-emerald-400">+{s.oee}pp</td>
                    <td className="py-2.5 px-3 text-right text-xs font-semibold text-amber-400">{fmtVnd(s.vnd)}</td>
                    <td className="py-2.5 px-3 text-right text-xs font-semibold text-teal-400">-{s.co2}kg</td>
                    <td className="py-2.5 px-3 text-right text-xs font-mono font-semibold text-text-primary">{fmtVnd(Math.round(s.efficiency))}</td>
                    <td className="py-2.5 px-3 text-center">
                      <button onClick={() => toggle(s.id)} data-testid={`oee-portfolio-rank-toggle-${s.id}`}>
                        {isOn
                          ? <CheckSquare size={16} className="text-primary" weight="fill" />
                          : <Square size={16} className="text-text-secondary" />}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Projected OEE Waterfall */}
      <div className="bg-surface border border-border rounded-lg p-5">
        <h3 className="font-heading text-sm font-semibold text-text-primary mb-1">Projected OEE Build-Up</h3>
        <p className="text-xs font-body text-text-secondary mb-4">How selected scenarios stack to reach projected OEE</p>
        <div className="flex items-end gap-1 h-48 px-4" data-testid="oee-portfolio-buildup">
          {/* Baseline bar */}
          <div className="flex-1 flex flex-col items-center justify-end">
            <p className="font-heading text-xs font-bold text-text-primary mb-1">{kpis.plant_oee_pct}%</p>
            <div className="w-full rounded-t-md bg-primary/30 transition-all duration-500" style={{ height: `${(kpis.plant_oee_pct / 100) * 100}%` }} />
            <p className="text-[9px] font-body text-text-secondary mt-1.5 text-center">Baseline</p>
          </div>
          {/* Each selected scenario contribution */}
          {selectedScenarios.map((s) => {
            const catColor = CATEGORY_COLORS[s.category] || '#6b7280';
            return (
              <div key={s.id} className="flex-1 flex flex-col items-center justify-end">
                <p className="font-heading text-[10px] font-bold mb-1" style={{ color: catColor }}>+{s.oee}</p>
                <div className="w-full rounded-t-md transition-all duration-500" style={{
                  height: `${Math.max((s.oee / 100) * 100 * 3, 4)}%`,
                  backgroundColor: catColor,
                }} />
                <p className="text-[7px] font-body text-text-secondary mt-1.5 text-center truncate w-full" title={s.title}>
                  {s.id}
                </p>
              </div>
            );
          })}
          {/* Target bar */}
          <div className="flex-1 flex flex-col items-center justify-end">
            <p className="font-heading text-xs font-bold text-emerald-400 mb-1">{projectedOee.toFixed(1)}%</p>
            <div className="w-full rounded-t-md bg-emerald-500/40 border-2 border-dashed border-emerald-400/50 transition-all duration-500" style={{ height: `${(projectedOee / 100) * 100}%` }} />
            <p className="text-[9px] font-body text-emerald-400 mt-1.5 text-center font-semibold">Projected</p>
          </div>
        </div>
        {kpis.target_oee_pct && (
          <div className="mt-3 flex items-center justify-center gap-2 text-xs font-body">
            <Target size={14} className="text-primary" weight="duotone" />
            <span className="text-text-secondary">Plant target: <span className="font-semibold text-text-primary">{kpis.target_oee_pct}%</span></span>
            <span className="text-border">|</span>
            <span className={projectedOee >= kpis.target_oee_pct ? 'text-emerald-400 font-semibold' : 'text-red-400 font-semibold'}>
              {projectedOee >= kpis.target_oee_pct
                ? `Exceeds target by ${(projectedOee - kpis.target_oee_pct).toFixed(1)} pp`
                : `Gap to target: ${(kpis.target_oee_pct - projectedOee).toFixed(1)} pp`
              }
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
